<script lang="ts" setup>
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { apiService } from "@/services/services";
import PromiseWrapper from "@/shared/components/PromiseWrapper.vue";
import type { PoolListItem } from "@/models/pool-list-item.model";
import PoolLine from "@/features/pools/PoolLine.vue";
import KlereoIcon from "@/shared/components/KlereoIcon.vue";
import { Nullable } from "@/models/nullable.type";
import PoolsFilters from "@/features/pools/PoolsFilters.vue";
import { debounce } from "lodash";
import PoolIcon from "@/features/pools/PoolIcon.vue";
import { requestLEScan } from "@/features/ble/utils/requestLEScan.ts";
import { ScanResult } from "@capacitor-community/bluetooth-le";
import { useMutation } from "@tanstack/vue-query";
import { lastValueFrom, tap } from "rxjs";
import OfflinePoolsList from "@/features/ble/public/OfflinePoolsList.vue";
import BleScanResult from "@/features/ble/scan/BleScanResult.vue";
import { useStoredBlePools } from "@/features/ble/utils/localStorage.ts";
import { Capacitor } from "@capacitor/core";

const showFilters = ref(false);
const firstPagePromise = ref<Nullable<Promise<unknown>>>(undefined);
const pools = ref<PoolListItem[]>([]);
const pageSize = ref(60);
const pageIdx = ref(0);
const query = ref(history.state.query ?? "");
const debouncing = ref(false);
const loading = ref(true);
const hasMore = ref(true);
const filter = ref(history.state.filter ?? "");
const sort = ref<"p" | "P" | "a" | "A" | undefined>(undefined);

const loadNextPage = (replace = false) => {
  loading.value = true;
  return apiService
    .listPools(
      pageSize.value,
      pageIdx.value * pageSize.value,
      query.value,
      filter.value,
      sort.value
    )
    .then(({ morePool, response }) => {
      hasMore.value = morePool;
      pageIdx.value++;
      pools.value = replace ? response : [...pools.value, ...response];
      loading.value = false;
      debouncing.value = false;
    });
};

const reset = () => {
  hasMore.value = true;
  pageIdx.value = 0;
  return loadNextPage(true).then(handleScroll);
};

const debouncedReset = debounce(() => {
  return reset();
}, 1000);

const scrollable = useTemplateRef<HTMLDivElement>("scrollable");
const handleScroll = () => {
  if (loading.value || !hasMore.value) {
    return;
  }

  if (
    scrollable.value!.scrollTop + scrollable.value!.clientHeight >
      scrollable.value!.scrollHeight - 10 ||
    scrollable.value!.scrollHeight === scrollable.value!.clientHeight
  ) {
    loadNextPage().then(handleScroll);
  }
};

onMounted(() => {
  firstPagePromise.value = loadNextPage().then(handleScroll);
  scrollable.value!.addEventListener("scroll", handleScroll);
});

watch(filter, () => {
  debouncing.value = true;
  return debouncedReset();
});

watch(query, () => {
  debouncing.value = true;
  return debouncedReset();
});

// TODO could mutualize with BlePublicView
const devices = ref([] as ScanResult[]);
const addResult = (result: ScanResult) => {
  const existingIdx = devices.value.findIndex(
    (device) => device.device.deviceId === result.device.deviceId
  );
  if (existingIdx !== -1) {
    devices.value[existingIdx] = result;
  } else {
    devices.value.push(result);
    console.log("found device");
  }
};
const scanMutation = useMutation({
  mutationFn: async () => {
    devices.value = [];
    return lastValueFrom(requestLEScan().pipe(tap(addResult)), {
      defaultValue: null,
    });
  },
});

onMounted(() => {
  scanMutation.mutate();
});

const excludedDeviceIds = computed(() =>
  devices.value.map((d) => d.device.deviceId)
);

const storedPools = useStoredBlePools();

const knownDevices = computed(() =>
  devices.value.filter((device) =>
    storedPools.value.some(
      (storedPool) => storedPool.deviceId === device.device.deviceId
    )
  )
);

const isNativePlatform = Capacitor.isNativePlatform();

defineExpose({ refresh: reset });
</script>

<template>
  <div class="flex flex-col h-full relative">
    <div class="flex mb-5 mx-5">
      <klereo-icon name="search" class="w-[27px]"></klereo-icon>
      <input class="flex-grow mx-3 form-input" v-model="query" />
      <klereo-icon
        name="sliders"
        @click="showFilters = !showFilters"
        class="w-[27px]"
      ></klereo-icon>
      <div
        v-show="filter?.length"
        class="absolute h-3 w-3 rounded-md bg-red-500 right-[-5px] mt-[-5px]"
      ></div>
    </div>

    <div
      v-show="showFilters"
      class="fixed inset-0 z-10"
      @click="showFilters = false"
    ></div>

    <div
      v-show="showFilters"
      class="absolute bg-opacity-90 bg-darkgrey z-50 top-[30px] right-0 left-0 p-4 pb-0 rounded-b-2xl"
    >
      <pools-filters @change="(f) => (filter = f)" />
    </div>

    <div
      class="flex-shrink-[10000] overflow-auto px-5"
      ref="scrollable"
      :class="{ 'opacity-50': loading || debouncing }"
    >
      <promise-wrapper :promise="firstPagePromise">
        <div
          class="grid gap-2 pool-list sm:grid-cols-2 lg:grid-cols-3 flex-shrink overflow-auto"
        >
          <div
            v-if="isNativePlatform"
            class="card p-4 cursor-pointer flex items-center"
            @click="
              $router.push({
                path: '/ble',
              })
            "
          >
            <div
              class="flex-grow-0 flex-shrink-0"
              style="width: 32px; height: 32px"
            >
              <PoolIcon :is-ble="true" />
            </div>

            <div class="flex-shrink ml-3">
              <h1 class="text-md font-medium mb-0.5 flex">
                {{ $t("BleScanAction") }}
              </h1>
            </div>
          </div>

          <BleScanResult
            v-for="device of knownDevices"
            :key="device.device.deviceId"
            :result="device"
          />

          <div
            v-for="pool of pools"
            :key="pool.idSystem"
            class="card p-4 cursor-pointer"
            :class="{
              active: $route.params.poolId === pool.idSystem.toString(),
            }"
            @click="
              $router.push({
                path: `/pool/${pool.idSystem}`,
                state: { query, filter },
              });
              $emit('poolClick');
            "
          >
            <pool-line :pool="pool" />
          </div>
          <div v-if="loading || debouncing" class="text-white">Loading…</div>
          <div v-if="!loading && !pools.length" class="text-white">
            {{ $t("NoPoolMatchingFilters") }}
          </div>

          <OfflinePoolsList :excluded-device-ids="excludedDeviceIds" />
        </div>
      </promise-wrapper>
    </div>
  </div>
</template>

<style>
.sidebar .pool-list {
  @apply grid-cols-1 !important;
}
</style>
